import { useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatWeight,
  isPureBodyweightPr,
  type PRRecord,
  type PRsByExercise,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";

// Per-exercise rep-max records, shown on the catalog detail. THE home for
// the 1/3/5/8/10/12-RM data: looking up an exercise shows its records here
// instead of a long all-exercises table on Progress. Self-contained — it
// fetches /api/me/records (all-time, via the injected apiFetch) and reads
// its own exercise. Vertical label+value rows (not a dense grid) for
// reduced-vision readability ([[feedback_user_visual_accessibility]]).

// The rep buckets PR records are tracked for (mirrors the mobile
// progressScreenMath constant — kept local so the shared card has no
// cross-package math dependency).
const REP_BUCKETS: readonly number[] = [1, 3, 5, 8, 10, 12];

type RecordsResponse = { records: PRsByExercise; scope: "all" | "year" };

type State =
  | { kind: "loading" }
  | { kind: "ready"; byRep: Record<number, PRRecord> | null }
  | { kind: "error" };

export type CatalogRecordsCardProps = {
  theme: UiTheme;
  slug: string;
  unit: "kg" | "lb";
  locale: string;
  apiFetch: ProgramApiFetch;
  formatDate: (iso: string, locale: string) => string;
};

export function CatalogRecordsCard({
  theme,
  slug,
  unit,
  locale,
  apiFetch,
  formatDate,
}: CatalogRecordsCardProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      try {
        const res = await apiFetch<RecordsResponse>("/api/me/records");
        if (cancelled) return;
        setState({ kind: "ready", byRep: res.records[slug] ?? null });
      } catch {
        if (!cancelled) setState({ kind: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, apiFetch]);

  const rows =
    state.kind === "ready" && state.byRep !== null
      ? REP_BUCKETS.map((rm) => ({ rm, pr: state.byRep?.[rm] ?? null })).filter(
          (x): x is { rm: number; pr: PRRecord } => x.pr !== null,
        )
      : [];

  return (
    <View style={styles.card}>
      <Text style={styles.cardLabel}>{t("mobile.catalog.recordsLabel")}</Text>
      {state.kind === "loading" ? (
        <ActivityIndicator color={theme.palette.ink3} />
      ) : state.kind === "error" ? (
        <Text style={styles.empty}>
          {t("mobile.catalog.recordsError", {
            defaultValue: "Could not load records.",
          })}
        </Text>
      ) : rows.length === 0 ? (
        <Text style={styles.empty}>{t("mobile.catalog.recordsEmpty")}</Text>
      ) : (
        rows.map(({ rm, pr }) => (
          <View key={rm} style={styles.row}>
            <Text style={styles.rmLabel}>{`${rm}RM`}</Text>
            {/* Pure-bodyweight rep PRs reuse the catalog history idiom
                instead of "0 kg" (the record is the reps, not the load). */}
            <Text style={styles.value} numberOfLines={1}>
              {isPureBodyweightPr(pr.weightKg)
                ? t("mobile.catalog.historyBodyweight", {
                    defaultValue: "{{reps}} reps BW",
                    reps: pr.reps,
                  })
                : formatWeight(pr.weightKg, unit, locale)}
            </Text>
            <Text style={styles.date} numberOfLines={1}>
              {formatDate(pr.setAt, locale)}
            </Text>
          </View>
        ))
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s3,
    },
    cardLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    empty: { color: p.ink3, fontSize: ty.sizes.sm },
    row: {
      flexDirection: "row",
      alignItems: "baseline",
      gap: s.s3,
      paddingTop: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
    },
    rmLabel: {
      width: 56,
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    value: {
      flex: 1,
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    date: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontVariant: ["tabular-nums"],
    },
  });
}
